import { DatePipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { AnotacionCalendario } from './utilidades/calendario.models';
import { capitalizar, formatearFecha } from '../utilidades/valores.utils';
import * as moment from 'moment';

@Component({
  selector: 'compartido-calendario',
  templateUrl: './calendario.component.html',
  styleUrls: ['./calendario.component.scss'],
  imports: [DatePipe, NgFor, NgIf, NgTemplateOutlet],
  standalone: true,
})
export class CalendarioComponent {
  /* Constantes */
  protected readonly DIAS_SEMANA = [
    'Dom',
    'Lun',
    'Mar',
    'Mie',
    'Jue',
    'Vie',
    'Sab',
  ];

  /* Anotaciones */
  public anotaciones = input<AnotacionCalendario[]>([]);
  public refAnotaciones = input<TemplateRef<{
    $implicit: AnotacionCalendario[];
  }> | null>(null);
  private readonly _mapaAnotaciones = computed(() => {
    const map: Map<string, AnotacionCalendario[]> = new Map();
    this.anotaciones().forEach((anotacion: AnotacionCalendario) => {
      const date = formatearFecha(anotacion.fecha);
      const anotaciones = map.get(date) || [];
      anotaciones.push(anotacion);
      map.set(date, anotaciones);
    });
    return map;
  });

  /* Emisores de eventos */
  protected cambioMes = output<Date>();
  protected clickDia = output<Date>();

  /* Fecha activa */
  protected readonly fechaActiva = signal(moment());
  protected readonly mesActivo = computed(
    () =>
      capitalizar(this.fechaActiva().locale('es').format('MMMM')) +
      ' ' +
      this.fechaActiva().format('YYYY'),
  );

  /* Rangos de fechas */
  protected readonly primerDia = computed(() => {
    return this.fechaActiva().clone().startOf('month').startOf('week');
  });
  protected readonly ultimoDia = computed(() => {
    return this.fechaActiva().clone().endOf('month').endOf('week');
  });

  /* Días a mostrar */
  private readonly _dias = computed(() => {
    const start = this.primerDia();
    const end = this.ultimoDia();
    const days: moment.Moment[] = [];
    for (
      let date = start.clone();
      date.isSameOrBefore(end);
      date.add(1, 'day')
    ) {
      days.push(date.clone());
    }
    return days;
  });

  /* Días con detalles */
  protected readonly diasDetallados = computed(() => {
    return this._dias().map((dia: moment.Moment) => {
      return {
        fecha: dia.toDate(),
        esHoy: dia.isSame(moment(), 'day'),
        esMes: dia.isSame(this.fechaActiva(), 'month'),
        anotaciones: this._mapaAnotaciones().get(formatearFecha(dia)) || [],
      };
    });
  });

  /**
   * Cambia el mes activo al mes siguiente.
   * @author Juan Corral
   */
  protected mesSiguiente(): void {
    this.fechaActiva.update((value) => value.clone().add(1, 'month'));
    this.cambioMes.emit(this.fechaActiva().toDate());
  }

  /**
   * Cambia el mes activo al mes anterior.
   * @author Juan Corral
   */
  protected mesAnterior(): void {
    this.fechaActiva.update((value) => value.clone().subtract(1, 'month'));
    this.cambioMes.emit(this.fechaActiva().toDate());
  }
}
